import atticBeforeAfter1 from './attic-before-after-1.png';
import atticBeforeAfter2 from './attic-before-after-2.png';
import { Helmet } from 'react-helmet';
import React, { useState } from 'react'
import './App.css'
import {
  BrowserRouter as Router,  // ✅ CHANGED FROM HashRouter
  Routes,
  Route,
  Link,
  useLocation,
  useParams,
} from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  CheckCircle,
  Shield,
  Home as HomeIcon,
  Droplet,
  Leaf,
  Fan,
  Wind,
  Sun,
  Hammer,
  Phone,
  Mail,
  MapPin,
  ClipboardList,
} from 'lucide-react'
const logo = '/logo.png'

// ✅ ALL BLOG POSTS LIVE HERE (shared by list + detail pages)
const blogPosts = [
  {
    slug: '7-crawlspace-insulation-mistakes',
    title: '7 Crawlspace Insulation Mistakes Homeowners Need to Avoid',
    summary:
      'Avoid these common crawlspace insulation errors to protect your home from moisture, mold, and wasted energy.',
    content: `
      <p>Crawlspace insulation is one of the most misunderstood parts of the home. Many homeowners assume that adding insulation automatically improves comfort, but the wrong materials or methods can trap moisture, grow mold, attract pests, and waste energy.</p>

      <p>Here are the seven most common crawlspace insulation mistakes to avoid:</p>

      <h3>1. Using Fiberglass Batt Insulation in a Humid Crawlspace</h3>
      <p>Fiberglass performs well in walls and attics—but not in crawlspaces. It absorbs moisture, sags, molds, and eventually falls down. Once saturated, it becomes useless and creates musty odors and air-quality problems.</p>

      <h3>2. Insulating the Floor Instead of the Crawlspace Walls</h3>
      <p>Older homes often have insulation between the joists. This traps moisture and does nothing to control humidity. Insulating the crawlspace walls brings the space into the home's conditioned envelope—improving efficiency, comfort, and moisture control.</p>

      <h3>3. Skipping Air Sealing Before Insulating</h3>
      <p>Gaps around vents, plumbing, wires, or rim joists allow humid outside air to pour in. If insulation goes over those gaps, moisture builds behind the insulation. Always seal penetrations before insulating.</p>

      <h3>4. Ignoring the Rim Joists</h3>
      <p>Rim joists are one of the leakiest areas of the home. Leaving them uninsulated invites drafts, moisture, and pests. Sealing and insulating rim joists improves comfort, reduces humidity, and protects your structure.</p>

      <h3>5. Covering the Termite Inspection Gap</h3>
      <p>Florida requires a visible inspection strip at the top of the foundation wall. If insulation covers this area, pest control companies cannot inspect for termites—and they may void your bond. Always leave the proper inspection gap.</p>

      <h3>6. Insulating Before Solving Moisture Problems</h3>
      <p>Insulation does NOT fix water intrusion, standing water, or wet wood. If the crawlspace is wet, insulation traps moisture and mold grows behind it. Moisture issues must be fixed before insulation goes in.</p>

      <h3>7. Not Sealing or Taping Insulation Seams</h3>
      <p>If rigid foam seams are not taped or foamed correctly, humid air leaks behind the boards. This defeats the purpose of insulation. Crawlspaces require proper seam sealing with the right tapes or mastic.</p>

      <p>Avoiding these mistakes ensures a healthier, more efficient crawlspace. At Florida Healthy Home Solutions, we use building-science-based methods to insulate crawlspaces correctly and protect your home long-term.</p>
    `,
  },

  {
    slug: 'encapsulated-vs-vented-crawlspaces',
    title: 'Encapsulated vs. Vented Crawlspaces in Florida',
    summary:
      'Why sealed, conditioned crawlspaces perform better than vented ones in hot, humid North Florida.',
    content:
      'In Florida\'s humid climate, vented crawlspaces often draw in warm, moist air that condenses on cooler surfaces. This leads to high humidity, mold growth, and wood rot. Encapsulation creates a sealed environment using vapor barriers, wall insulation, and a dehumidifier. This not only stabilizes humidity but also improves indoor air quality and can lower energy costs.',
  },
  {
    slug: 'why-hardwood-floors-buckle',
    title: 'Why Hardwood Floors Buckle Over Damp Crawlspaces',
    summary:
      'How moisture from below affects wood floors, and how encapsulation and dehumidification help them relax back down.',
    content:
      'Hardwood floors expand when they absorb excess moisture, often caused by damp crawlspaces underneath. Over time, this moisture causes cupping and buckling. Once the crawlspace is sealed and humidity is controlled, wood flooring gradually stabilizes. Encapsulation is a key step in protecting your investment in flooring.',
  },
  {
    slug: 'stack-effect-explained',
    title: 'The Stack Effect: How Crawlspace Air Reaches Your Living Room',
    summary:
      'Moisture, odors, and spores do not stay under the house. We explain how they move upward and how to control it.',
    content:
      'The stack effect is a natural airflow pattern where warm air rises through your home, pulling air from lower spaces like the crawlspace. If your crawlspace air is damp or moldy, that air moves upward into living areas. Encapsulation and balanced ventilation systems prevent this by sealing air leaks and controlling humidity.',
  },
  {
    slug: 'do-you-need-a-dehumidifier',
    title: 'Do You Need a Dehumidifier in an Encapsulated Crawlspace?',
    summary:
      'Encapsulation alone is not always enough. We cover when and why to add mechanical dehumidification.',
    content: `
While vapor barriers stop ground moisture, residual humidity can still build up inside an encapsulated crawlspace. Installing a dehumidifier helps keep humidity levels below about 55%, preventing mold growth and protecting the structure. It's especially important in Florida's coastal and inland humid zones.
`,

  },

  // "Why We Do It" stories
  {
    slug: 'cleaning-and-preparation',
    title: 'Cleaning & Preparation: Starting with a Fresh Crawlspace',
    summary:
      'Why the first step in any project is removing debris, old insulation, and hazards before anything else happens.',
    content:
      'We once inspected a crawlspace where a previous contractor had laid new plastic right over old trash, fallen insulation, and even food wrappers. The homeowner could not understand why the space still smelled bad. When we pulled back the plastic, we found moldy debris trapped underneath. That is why we always begin by removing debris, old insulation, and hazards. A clean, prepared crawlspace is the only solid foundation for long-term moisture control.',
  },
  {
    slug: 'grading-the-crawlspace',
    title: 'Grading the Crawlspace So Water Has Somewhere to Go',
    summary:
      'A simple slope can be the difference between a dry crawlspace and a mud pit.',
    content:
      'Imagine pouring water onto a wrinkled tarp: it just sits in the low spots. The same thing happens in an ungraded crawlspace. We met a homeowner whose crawlspace looked encapsulated at first glance, but standing water kept appearing in random corners. The soil had never been graded. Once we re-graded the space so water drained toward a sump basin, the standing water disappeared and the dehumidifier could finally keep up.',
  },
  {
    slug: 'outlets-and-lights',
    title: 'Why We Add Outlets and Lights Under Your Home',
    summary:
      'It is not just a convenience; it is required for safe service and long-term maintenance.',
    content:
      'A customer once told us their previous contractor installed a dehumidifier but left no outlet nearby. The cord was draped through a vent and plugged in outside. Every time it rained hard, they lost power to the unit. We install proper outlets and lighting in the crawlspace so equipment can be safely powered and serviced. Good lighting also makes future inspections safer for you and for us.',
  },
  {
    slug: 'sealing-vents-and-gaps',
    title: 'Sealing Vents and Gaps: Keeping Humid Florida Air Out',
    summary:
      'Open vents in a humid climate are like leaving a window open in a rainstorm.',
    content:
      'One homeowner thought their vented crawlspace was "breathing" correctly because they could feel air moving. Unfortunately, that air was 90% humidity. During summer, the cool surfaces under the house became perfect condensing points. We sealed the vents and penetrations, then added a dehumidifier. Within weeks, the musty smell in the home vanished and the crawlspace stabilised at a healthy humidity level.',
  },
  {
    slug: 'drying-before-encapsulation',
    title: 'Drying the Crawlspace Before Encapsulation',
    summary:
      'Encapsulating wet wood is like painting over a leak—it hides the problem instead of fixing it.',
    content:
      'We were called to a project where a different company had encapsulated a crawlspace while the joists were still soaked from a prior leak. The plastic looked new, but moisture readings in the wood were dangerously high. That trapped moisture began feeding mold behind the "finished" system. Our process includes temporary dehumidification and moisture testing to be sure the lumber is dry before we seal anything in.',
  },
  {
    slug: 'mold-removal',
    title: 'Mold Removal: Wiping the Slate Clean',
    summary:
      'You do not want to lock existing mold colonies under a brand new system.',
    content:
      'A family complained that even after a basic moisture fix, their crawlspace still smelled musty. When we inspected, we found visible mold on joists and subflooring that had never been removed. Simply reducing humidity is not enough if colonies are already active. We carefully clean affected areas first so your new system starts with a fresh, healthy structure instead of sealing problems in place.',
  },
  {
    slug: 'mold-prevention-treatment',
    title: 'Mold Prevention Treatment: Protecting the Wood Long-Term',
    summary:
      'After cleaning, we add a protective layer so the wood is not an easy target next time humidity spikes.',
    content:
      'Think of mold prevention treatment like a clear shield on your phone: you may not see it, but it is working in the background. In one coastal home, a sudden plumbing leak briefly raised humidity. Because we had already applied a preventative treatment to the joists, the wood resisted new growth even during that stressful event. Once the leak was fixed and the dehumidifier caught back up, the crawlspace remained clean.',
  },
  {
    slug: 'rim-joist-insulation',
    title: 'Rim Joist Insulation: Closing One of the Biggest Leaks',
    summary:
      'Uninsulated rim joists leak air, energy, and comfort right where you feel it most—at your floors.',
    content:
      'A homeowner in an older house kept turning up the thermostat because their floors were always chilly. When we inspected the crawlspace, the rim joists were bare and full of gaps. We sealed and insulated them, and the difference was immediate: fewer drafts, quieter rooms, and a more efficient dehumidifier because outside air was no longer pouring in around the perimeter.',
  },
  {
    slug: 'insulate-walls-not-floor',
    title: 'Why We Insulate Crawlspace Walls Instead of the Floor',
    summary:
      'Bringing the crawlspace into the conditioned envelope stabilises the whole home.',
    content: `
Many people assume the insulation should go between the floor joists. In Florida's climate, insulating the crawlspace walls instead is a better strategy. On one project, we converted a failing fiberglass-in-the-joists setup to rigid foam on the walls. The crawlspace became part of the home's conditioned space, the pipes were better protected, and the dehumidifier did not have to fight as hard to control humidity.
`,

  },
  {
    slug: 'vapor-barrier-and-termite-gap',
    title: 'Why a 12-Mil Vapor Barrier with Termite Gap Matters',
    summary:
      'This is the heart of the system—and it has to be thick, sealed, and inspectable.',
    content:
      'We were called after a thin, pieced-together plastic had been used as a "vapor barrier." The seams were loose and piers were never wrapped. Moisture readings were still high, and the pest company could not see the foundation for termite checks. We replaced it with a 12-mil barrier, sealed seams, wrapped piers, and left a proper 3-inch inspection gap at the top. Moisture dropped and the termite bond was back in good standing.',
  },
  {
    slug: 'dehumidifier-and-condensate-pump',
    title: 'Dehumidifier & Condensate Pump: The Engine of the System',
    summary:
      'Without active humidity control, even the best liner will eventually lose the battle.',
    content:
      'One homeowner proudly showed us their new liner but could not understand why their humidity was still in the 80% range. They had no dehumidifier. Ground moisture was reduced, but the air still carried humidity from outside and from small leaks. After installing a properly sized dehumidifier and condensate pump, the crawlspace stabilised below 60% humidity and stayed that way through the summer.',
  },
  {
    slug: 'airtight-crawlspace-doors',
    title: 'Airtight Crawlspace Doors: Small Opening, Big Impact',
    summary:
      'A leaky access panel can undo a lot of good work.',
    content:
      'We once found a beautifully encapsulated crawlspace with one big flaw: the access door was a warped piece of plywood with gaps all around it. Humid outdoor air was flowing in at that single weak point. After replacing it with an insulated, gasketed door, humidity levels became much easier to control and the conditioned air stayed where it belonged.',
  },

  // "What Not to Do" stories
  {
    slug: 'never-staple-plastic-to-joists',
    title: 'Why You Should Never Staple Plastic to the Floor Joists',
    summary:
      'A quick "fix" that quietly traps moisture against your subfloor.',
    content:
      'A contractor had stapled thin plastic directly to the joists in one home, claiming it would "protect the floor." Instead, moisture rising from below was trapped against the wood. When we inspected, the subfloor was speckled with mold and beginning to soften. Plastic belongs on the ground and walls as part of a designed system, never tacked to the ceiling of the crawlspace.',
  },
  {
    slug: 'thin-loose-plastic',
    title: 'The Problem with Thin, Loose Plastic on the Ground',
    summary:
      'If seams are open and the material is too thin, moisture simply finds its way through.',
    content:
      'We visited a crawlspace where the homeowner had tried DIY plastic from the home store. It was 4-mil, full of tears, and overlapped loosely. Moisture readings showed that almost as much vapor was coming through as if there were no barrier at all. Once we upgraded to a 12-mil liner with sealed seams and proper wall terminations, the difference in humidity was dramatic.',
  },
  {
    slug: 'leaving-vents-open',
    title: 'Why Leaving Vents Open Makes Things Worse in Florida',
    summary:
      'Ventilation sounds good, until you remember how humid our air is.',
    content:
      'A homeowner told us, "The vents are open so the crawlspace can breathe." Unfortunately, on a 95°F, 90% humidity day, that "breath" is pure moisture. We showed them how the cool surfaces under the house were collecting condensation from that outside air. After sealing the vents and adding controlled dehumidification, the space finally dried out and the musty smell went away.',
  },
  {
    slug: 'one-time-mold-sprays',
    title: 'One-Time Mold Sprays: Why We Do Not Rely on Them',
    summary:
      'Killing what you can see is not enough if humidity is still out of control.',
    content:
      'We often hear, "Another company said they would just fog the space once and be done." On one job, that is exactly what had happened: a one-time spray, no drying, no sealing, no dehumidifier. Within months, new mold was growing on the joists. Our approach is to address moisture first, then clean, then protect. Sprays alone are temporary; a full system is what keeps the crawlspace healthy.',
  },
  {
    slug: 'why-fiberglass-fails',
    title: 'Why Fiberglass Insulation Fails in Crawlspaces',
    summary:
      'It sags, soaks up moisture, and becomes a home for mold and critters.',
    content: `
We have pulled countless batts of fiberglass from crawlspaces that looked more like wet sponges than insulation. In one case, the batts were hanging down, full of mouse nests and mold. In Florida's humidity, fiberglass under the floor is almost always the wrong choice. Rigid foam or closed-cell spray foam on the walls performs far better and keeps the space cleaner.
`,

  },
  {
    slug: 'skipping-the-dehumidifier',
    title: 'Skipping the Dehumidifier: The Most Expensive Corner to Cut',
    summary:
      'Without it, humidity slowly creeps back and undoes the entire investment.',
    content:
      'A customer once told us, "The other quote was cheaper because they said we did not need a dehumidifier." That cheaper bid would have left them with a sealed crawlspace that still allowed humidity to build over time. The cost of fixing recurring mold and flooring damage far exceeds the price of a properly sized unit. That is why we design every encapsulation with long-term humidity control in mind.',
  },
  {
    slug: 'covering-termite-inspection-areas',
    title: 'Covering Termite Inspection Areas Can Cost You Later',
    summary:
      'Pest companies need to see the foundation—your liner should not block their view.',
    content: `
On one project, the termite company refused to renew a homeowner's bond because plastic had been run all the way up the wall, hiding the critical inspection area. We modified the system to include a clean 3-inch inspection gap at the top of the liner. The pest company was satisfied, the bond was reinstated, and the crawlspace still had excellent moisture protection.
`,

  },
  // Attic & roofline articles
  {
    slug: 'attic-insulation-mistakes-florida',
    title: '7 Attic Insulation Mistakes Florida Homeowners Should Avoid',
    summary:
      'Why the wrong attic insulation or air sealing approach can trap heat, invite moisture, and waste money in North Florida homes.',
    content: `
      <p>In Florida, the attic can easily reach 120–150°F in summer. If insulation and air sealing are done incorrectly, that heat and humidity can move down into the home, overwork your AC, and drive up energy bills.</p>

      <p>Here are some of the most common attic insulation mistakes we see in North Florida:</p>

      <h3>1. Relying on Thin or Settled Insulation</h3>
      <p>Many older homes still have a thin layer of insulation that has settled over time. It may look like it is “there,” but the R-value is nowhere near modern recommendations. Topping up or replacing old insulation can make a huge difference in comfort.</p>

      <h3>2. Ignoring Air Leaks Before Adding Insulation</h3>
      <p>Recessed lights, top plates, wire penetrations, and duct chases can leak huge amounts of air between the attic and living space. If you only add insulation without sealing these leaks, heat and humidity will still move into the home.</p>

      <h3>3. Piling Insulation Over Recessed Lights or Fixtures</h3>
      <p>Covering non-IC rated fixtures with insulation can be a fire risk and may also block airflow around fixtures that need to breathe. We always identify and treat these areas correctly before adding insulation.</p>

      <h3>4. Using the Wrong Type of Insulation for the Application</h3>
      <p>Blown-in fiberglass, cellulose, batt insulation, and spray foam all have a place—but not every product fits every attic. We match the insulation type to the roof design, mechanical systems, and your goals for comfort and efficiency.</p>

      <h3>5. Ignoring Ductwork in a Hot Attic</h3>
      <p>If your ducts run through the attic and are poorly insulated or leaky, they can pick up attic heat before the air ever reaches your rooms. Sealing and insulating ductwork is often just as important as insulating the attic floor or roof deck.</p>

      <h3>6. Blocking Important Inspection Areas</h3>
      <p>Just like in the crawlspace, pest inspectors and other professionals still need visual access to key areas. We insulate carefully so future inspections and service remain possible.</p>

      <h3>7. Skipping a Professional Assessment</h3>
      <p>Every attic is different. A quick DIY fix may miss hidden moisture sources, duct issues, or code requirements. A building-science-based inspection identifies the real causes of hot rooms, moisture problems, or high bills before you invest in the wrong solution.</p>

      <p>At Florida Healthy Home Solutions, we look at the crawlspace and attic together so your whole home works as a system, not as separate, competing spaces.</p>
    `,
  },
  {
    slug: 'spray-foam-roof-deck-vs-traditional-attic',
    title: 'Spray Foam Roof Deck vs. Traditional Attic Insulation in Florida',
    summary:
      'Should you insulate the attic floor or spray foam the roof deck? We compare comfort, cost, and building-science tradeoffs.',
    content: `
      <p>Many homeowners hear about spray foam in the roof deck and wonder if it is worth it. In Florida’s climate, converting a vented attic to a semi-conditioned space can dramatically reduce attic temperatures and help your AC work more efficiently—but it has to be designed correctly.</p>

      <p>With a traditional attic, insulation usually sits on the attic floor and the attic itself stays very hot. With a spray foam roof deck, the insulation moves to the roofline, and the attic becomes part of the home’s thermal envelope.</p>

      <p>Key considerations include:</p>
      <ul>
        <li>Location and condition of existing ductwork</li>
        <li>Roof design and ventilation details</li>
        <li>Moisture control and the need for attic dehumidification</li>
        <li>Budget and long-term energy savings</li>
      </ul>

      <p>We walk St. Augustine, Jacksonville, and surrounding homeowners through both options so you can choose the right balance of upfront cost and long-term performance.</p>
    `,
  },
  {
    slug: 'signs-your-attic-is-too-hot',
    title: '7 Signs Your Attic Is Overheating Your Home',
    summary:
      'How to tell when attic heat and humidity are driving up energy bills and making certain rooms uncomfortable.',
    content: `
      <p>In many North Florida homes, the crawlspace is only half the story. The attic above can quietly undo a lot of good work if it is extremely hot or humid. Because you rarely go up there, problems often go unnoticed.</p>

      <p>Common signs your attic is causing issues include:</p>
      <ul>
        <li>Rooms directly under the attic that are much hotter than the rest of the home</li>
        <li>AC that runs almost nonstop on hot days</li>
        <li>Very high summertime electric bills compared to neighbors</li>
        <li>Dark streaks or staining around ceiling registers</li>
        <li>Visible ductwork sweating or rusting in the attic</li>
        <li>Insulation that looks trampled, thin, or uneven</li>
        <li>A noticeable temperature jump when you open the attic hatch</li>
      </ul>

      <p>If you are seeing these warning signs, it is worth having the attic inspected along with the crawlspace. Often, a combination of air sealing, insulation upgrades, and in some cases spray foam or dehumidification can bring the whole home back into balance.</p>
    `,
  },
  {
    slug: 'attic-dehumidifiers-in-sealed-attics',
    title: 'Do You Need a Dehumidifier in a Sealed or Spray Foam Attic?',
    summary:
      'Why some semi-conditioned attics in Florida still need dedicated humidity control to stay healthy.',
    content: `
      <p>Sealing and insulating an attic at the roof deck can eliminate extreme temperatures, but it does not automatically control humidity. In our humid climate, moisture can still enter through small leaks, stored items, or even from the rest of the house.</p>

      <p>In some sealed attics, humidity levels stay within a safe range. In others—especially homes near the coast or with marginal AC performance—a small, dedicated attic dehumidifier is the missing piece that keeps the space dry and protects the roof structure, ducts, and insulation.</p>

      <p>During our inspections, we measure humidity and temperature in both the crawlspace and the attic. If a dehumidifier is recommended, we size and place it so it can be serviced easily and drain reliably, just like we do in crawlspaces.</p>
    `,
  },

]

// ---------------------------------------------

function PageTransition({ children }) {
  const location = useLocation()
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  )
}

function FadeIn({ children, className = '' }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  )
}

function Navbar() {
  const location = useLocation()
  const links = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/services', label: 'Services' },
    { to: '/gallery', label: 'Gallery' },
    { to: '/blog', label: 'Blog' },
    { to: '/contact', label: 'Contact' },
  ]

  const currentPath = location.pathname || '/'

  return (
    <nav className="navbar">
      <div className="navbar-inner">
        <div className="navbar-logo">
          <img
            src={logo}
            alt="Florida Healthy Home Solutions - Licensed Crawlspace Contractor"
            style={{ height: '160px', width: 'auto', marginRight: '0.5rem' }}
          />
          <span>Florida Healthy Home Solutions</span>
        </div>
        <div className="navbar-links">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={
                'navbar-link' +
                (currentPath === link.to ? ' navbar-link-active' : '')
              }
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  )
}

// ---------------- PAGES ----------------

function HomePage() {
  return (
    <>
      <Helmet>
        {/* BASIC SEO */}
        <title>Crawlspace Encapsulation Jacksonville & St. Augustine FL | Licensed Contractor</title>
        <meta
          name="description"
          content="Licensed crawlspace encapsulation contractor serving Jacksonville, St. Augustine & Northeast Florida. Vapor barriers, dehumidifiers, insulation & moisture control. Free inspection!"
        />

        {/* CANONICAL URL */}
        <link rel="canonical" href="https://flhealthyhomesolutions.com/" />

        {/* ROBOTS TAG */}
        <meta name="robots" content="index, follow" />

        {/* OPEN GRAPH */}
        <meta property="og:title" content="Crawlspace Encapsulation Jacksonville & St. Augustine FL" />
        <meta
          property="og:description"
          content="Licensed contractor providing crawlspace encapsulation, vapor barriers, and moisture control in Northeast Florida."
        />
        <meta property="og:url" content="https://flhealthyhomesolutions.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Florida Healthy Home Solutions" />
        <meta property="og:image" content="https://flhealthyhomesolutions.com/social-share.jpg" />

        {/* TWITTER CARD */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Crawlspace Encapsulation Jacksonville & St. Augustine FL" />
        <meta
          name="twitter:description"
          content="Protect your home from moisture, mold, and humidity with crawlspace encapsulation in Northeast Florida."
        />
        <meta name="twitter:image" content="https://flhealthyhomesolutions.com/social-share.jpg" />

        {/* LOCAL BUSINESS SCHEMA */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HomeAndConstructionBusiness",
            "name": "Florida Healthy Home Solutions",
            "image": "https://flhealthyhomesolutions.com/logo.png",
            "description": "Licensed crawlspace encapsulation, vapor barriers, and moisture control services in Northeast Florida",
            "@id": "https://flhealthyhomesolutions.com",
            "url": "https://flhealthyhomesolutions.com",
            "telephone": "904-238-4416",
            "email": "frederic@flhealthyhomesolutions.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "1200 Eagle Point Dr",
              "addressLocality": "Saint Augustine",
              "addressRegion": "FL",
              "postalCode": "32092",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 29.8986,
              "longitude": -81.3124
            },
            "priceRange": "$$",
            "areaServed": [
              {"@type": "City", "name": "Jacksonville"},
              {"@type": "City", "name": "St. Augustine"},
              {"@type": "City", "name": "Palm Coast"},
              {"@type": "AdministrativeArea", "name": "St. Johns County"},
              {"@type": "AdministrativeArea", "name": "Clay County"},
              {"@type": "AdministrativeArea", "name": "Putnam County"},
              {"@type": "AdministrativeArea", "name": "Flagler County"},
              {"@type": "AdministrativeArea", "name": "Volusia County"}
            ]
          })}
        </script>
      </Helmet>

   {/* HERO */}
<section className="hero">
  <div className="hero-overlay">
    <FadeIn className="hero-content">
      <h1 className="hero-title">
        Protect Your Home from Moisture, Heat, Mold & Humidity
      </h1>
      <p className="hero-subtitle">
        Florida’s Crawlspace & Attic Conditioning Specialists
      </p>
      <p className="hero-text">
        A healthy home starts with a dry crawlspace and a properly conditioned attic. 
        We solve moisture, heat, and air-quality problems from the ground up to the roofline.
      </p>
      <Link to="/contact">
        <button className="btn-primary">
          Schedule Your Free Crawlspace & Attic Evaluation
        </button>
      </Link>
      <div className="hero-badges">
        <span>Licensed FL Residential Contractor</span>
        <span>•</span>
        <span>Certified Building Analyst Professional</span>
        <span>•</span>
        <span>Insured & Local</span>
      </div>
    </FadeIn>
  </div>
</section>


     {/* WHY YOUR CRAWLSPACE MATTERS */}
<section className="section section-light">
  <FadeIn className="section-header">
    <h2>Why Your Crawlspace & Attic Matter</h2>
    <p>
      The air you breathe on the first floor often comes from the crawlspace — and the 
      extreme heat in an unconditioned attic also affects comfort, energy bills, and 
      moisture levels inside your home. When both areas stay dry, clean, and properly 
      insulated, your entire home becomes healthier and more efficient.
    </p>
  </FadeIn>

  <div className="grid-three">
    <FadeIn>
      <div className="card icon-card">
        <Shield className="icon" />
        <h3>We Inspect</h3>
        <p>
          Detailed inspections of crawlspaces and attics, with photos and clear 
          explanations so you understand the real causes of moisture, heat, or 
          air-quality issues.
        </p>
      </div>
    </FadeIn>

    <FadeIn>
      <div className="card icon-card">
        <Hammer className="icon" />
        <h3>We Repair</h3>
        <p>
          From humidity and mold concerns to structural issues and inadequate 
          insulation, we fix problems using building-science-based solutions.
        </p>
      </div>
    </FadeIn>

    <FadeIn>
      <div className="card icon-card">
        <HomeIcon className="icon" />
        <h3>We Protect</h3>
        <p>
          Encapsulation, spray foam attic insulation, drainage, ventilation, and 
          humidity control systems work together to protect your home long-term.
        </p>
      </div>
    </FadeIn>
  </div>
</section>


   {/* TESTIMONIALS */}
<section className="section section-white">
  <FadeIn className="section-header">
    <h2>What North Florida Homeowners Are Saying</h2>
    <p>
      Homeowners and real estate professionals trust Florida Healthy Home Solutions 
      to solve crawlspace and attic problems that affect comfort, air quality, and 
      moisture control—quickly, professionally, and with building-science expertise.
    </p>
  </FadeIn>

  <div className="grid-three">

    <FadeIn>
      <div className="card testimonial-card">
        <p className="quote">
          "Our hardwood floors stopped cupping after they encapsulated our crawlspace. 
          The whole house smells fresher and feels drier. Best investment we've made 
          in our home."
        </p>
        <p className="author">— Melissa T., St. Augustine</p>
      </div>
    </FadeIn>

    <FadeIn>
      <div className="card testimonial-card">
        <p className="quote">
          "They explained everything with photos from under the house and in the attic. 
          No pressure—just honest options. The moisture readings dropped immediately 
          after the work was done."
        </p>
        <p className="author">— David R., Jacksonville</p>
      </div>
    </FadeIn>

    <FadeIn>
      <div className="card testimonial-card">
        <p className="quote">
          "We had extreme heat and humidity in our attic. They added spray foam to the 
          roof deck, sealed the leaks, and installed a small dehumidifier. The attic is 
          30 degrees cooler and our AC runs half as much now."
        </p>
        <p className="author">— Elizabeth M., Ponte Vedra</p>
      </div>
    </FadeIn>

    {/* NEW #4 */}
    <FadeIn>
      <div className="card testimonial-card">
        <p className="quote">
          "Our crawlspace was constantly damp, and our insulation was falling down. 
          Frederic and his team encapsulated it, installed a dehumidifier, and now the 
          musty smell in our living room is completely gone."
        </p>
        <p className="author">— Jason H., Green Cove Springs</p>
      </div>
    </FadeIn>

    {/* NEW #5 */}
    <FadeIn>
      <div className="card testimonial-card">
        <p className="quote">
          "After insulating and conditioning our attic, our upstairs is finally the same 
          temperature as the rest of the house. Huge difference. Honest, professional, 
          and worth every penny."
        </p>
        <p className="author">— Amanda L., St. Johns</p>
      </div>
    </FadeIn>

    <FadeIn>
      <div className="card testimonial-card">
        <p className="quote">
          "As a realtor, I need crawlspace and attic issues handled fast and correctly. 
          Florida Healthy Home Solutions is the only company I confidently recommend."
        </p>
        <p className="author">— Sarah P., Local Realtor</p>
      </div>
    </FadeIn>

  </div>
</section>

    </>
  )
}

function AboutPage() {
  return (
    <>
      <Helmet>
        <title>About Us | Licensed Crawlspace Contractor in Northeast Florida</title>
        <meta
          name="description"
          content="Florida Healthy Home Solutions is a licensed residential contractor (RR282812267) and certified building analyst serving Jacksonville, St. Augustine, and Northeast Florida with 25+ years experience."
        />
        <link rel="canonical" href="https://flhealthyhomesolutions.com/about" />
      </Helmet>

      <section className="section section-light">
        <FadeIn className="section-header">
          <h2>About Florida Healthy Home Solutions</h2>
        </FadeIn>
        <FadeIn className="section-text-wide">
          <p>
            Florida Healthy Home Solutions was founded with one mission: to protect families
            and homes across North Florida from the hidden dangers of moisture, mold, and
            poor air quality. We combine building science expertise with eco-friendly
            materials to create healthy, comfortable, and efficient homes from the ground up.
          </p>
          <p>
            The company's owner is a seasoned <strong>Construction Engineer</strong> with
            more than 25 years of experience in the Florida construction industry, ensuring
            every project is completed with a deep understanding of building science and
            structural integrity.
          </p>
          <p>
            We are also a <strong>Registered Florida Residential Contractor</strong> (
            <a
              href="https://www.myfloridalicense.com/LicenseDetail.asp?SID=&id=cabcda678c0f7708c6e4d57070e3bf28"
              target="_blank"
              rel="noopener noreferrer"
            >
              License No. RR282812267
            </a>
            ) and a <strong>Certified Building Analyst Professional</strong>, which means we
            look at your home as a complete system — from crawlspace to attic — to improve
            comfort, energy efficiency, and indoor air quality.
          </p>
          <p>
            We proudly serve homeowners throughout Northeast Florida — including Duval,
            Clay, St. Johns, Putnam, Flagler, and Volusia Counties — helping protect homes
            from humidity, mold, and energy loss with advanced insulation and
            moisture-control solutions.
          </p>
        </FadeIn>

        <FadeIn className="section-subheader">
          <h3>Why Choose Us</h3>
        </FadeIn>
        <FadeIn className="section-list">
          <ul>
            <li>✔️ Owner with 25+ years of hands-on construction and engineering experience.</li>
            <li>✔️ Licensed Florida Residential Contractor &amp; Certified Building Analyst Professional.</li>
            <li>✔️ Building-science approach for long-lasting results.</li>
            <li>✔️ Solutions tailored to Florida's humidity, soil, and coastal conditions.</li>
            <li>✔️ Honest inspections, clear communication, and no pushy sales tactics.</li>
            <li>✔️ Local team that stands behind our work with strong service and follow-up.</li>
          </ul>
        </FadeIn>
      </section>
    </>
  )
}

function ServicesPage() {
  const services = [
    {
      icon: Droplet,
      title: 'Crawlspace Encapsulation',
      desc: 'Full moisture control with heavy-duty vapor barriers, insulation, and sealing.',
    },
    {
      icon: HomeIcon,
      title: 'Crawlspace Insulation',
      desc: 'Rigid foam or spray foam insulation for walls and subfloors, improving comfort and efficiency.',
    },
    {
      icon: CheckCircle,
      title: 'Vapor Barrier Installation',
      desc: 'Durable 12–20 mil liners sealed and anchored to stop ground moisture intrusion.',
    },
    {
      icon: Fan,
      title: 'Dehumidifier Installation',
      desc: 'Commercial-grade units maintain humidity below 55% year-round.',
    },
    {
      icon: Wind,
      title: 'Crawlspace Ventilation',
      desc: 'Installation of code-compliant ventilation systems and exhaust fans to manage air quality and pressure balance.',
    },
    {
      icon: Hammer,
      title: 'Structural Repairs',
      desc: 'As a licensed Florida residential contractor, we perform structural repairs to restore and reinforce crawlspace supports, beams, and joists.',
    },
    {
      icon: ClipboardList,
      title: 'Crawlspace Service Contracts',
      desc: 'Annual or semi-annual inspections, dehumidifier checks, and vapor barrier evaluations to keep your system working long-term.',
    },
    {
      icon: Sun,
      title: 'Drainage & Sump Pump Systems',
      desc: 'Protects from groundwater intrusion and ensures a dry, stable crawlspace.',
    },
    {
      icon: Leaf,
      title: 'Preventative Mold Protection & Spot Treatment',
      desc: 'Cleaning of small, visible areas of mold growth and application of a preventative moisture barrier. Work is limited to minor, localized areas under Florida law; larger issues are handled by licensed mold remediation professionals.',
    },

    // 🔹 ATTIC SERVICES 🔹
    {
      icon: Shield,
      title: 'Attic Insulation Removal',
      desc: 'Safe removal of old, damaged, or contaminated attic insulation to prepare for a clean, efficient upgrade.',
    },
    {
      icon: Sun,
      title: 'Blown-In Attic Insulation',
      desc: 'Installation of high-R-value blown-in insulation to improve comfort and reduce cooling costs in Florida homes.',
    },
    {
      icon: Wind,
      title: 'Attic Air Sealing',
      desc: 'Sealing gaps, top plates, and penetrations in the attic to stop hot air intrusion and improve the building envelope.',
    },
    {
      icon: Droplet,
      title: 'Spray Foam Roof Deck Insulation',
      desc: 'Closed-cell or open-cell spray foam applied to the roof deck to create a semi-conditioned attic and lower attic temperatures.',
    },
    {
      icon: Sun,
      title: 'Attic Radiant Barrier Installation',
      desc: 'Radiant barrier installed at the roofline to reflect heat away from the attic and reduce peak summer temperatures.',
    },
    {
      icon: Leaf,
      title: 'Attic Mold Prevention & Spot Treatment',
      desc: 'Cleaning and treating small, visible mold areas in the attic and applying protective coatings to help prevent regrowth.',
    },
    {
      icon: Fan,
      title: 'Attic Duct Sealing & Insulation',
      desc: 'Sealing and insulating attic ductwork to reduce heat gain, improve AC performance, and cut energy bills.',
    },
    {
      icon: Shield,
      title: 'Attic Pest & Rodent Clean-Up',
      desc: 'Light-duty clean-up of droppings and contaminated insulation after pest exclusion.',
    },
    {
      icon: Fan,
      title: 'Attic Dehumidifier Installation',
      desc: 'Dedicated dehumidifiers for sealed or spray-foam attics to control humidity and protect against moisture issues.',
    },
    {
      icon: Hammer,
      title: 'Attic Storage Platforms & Walkways',
      desc: 'Installation of walkways and small storage platforms so you can access equipment without crushing insulation.',
    },
  ]

  return (
    <>
      <Helmet>
  <title>
    Crawlspace & Attic Services | Encapsulation, Attic Insulation, Spray Foam & Dehumidifiers
  </title>

  <meta
    name="description"
    content="Full crawlspace and attic services: encapsulation, vapor barriers, attic insulation, spray foam roof deck insulation, dehumidifiers, structural repairs, and building-science-based moisture control in Jacksonville, St. Augustine & Northeast Florida."
  />

  <link
    rel="canonical"
    href="https://flhealthyhomesolutions.com/services"
  />
</Helmet>

<section className="section section-white">
  <FadeIn className="section-header">
    <h2>Crawlspace & Attic Services</h2>
    <p>
      We protect your home from the two most overlooked problem areas—
      the crawlspace and the attic. From moisture control and encapsulation
      to attic conditioning and insulation upgrades, we provide complete
      building-science solutions that improve comfort, air quality, and
      energy performance.
    </p>
  </FadeIn>

  <div className="grid-three">
    {services.map((s, i) => (
      <FadeIn key={i}>
        <div className="card service-card">
          <s.icon className="icon" />
          <h3>{s.title}</h3>
          <p>{s.desc}</p>
        </div>
      </FadeIn>
    ))}
  </div>

  <FadeIn className="section-disclaimer">
    <p>
      <strong>Disclaimer:</strong> Florida Healthy Home Solutions is not a
      licensed mold remediation contractor. Any mold prevention or spot
      treatment services we provide are limited to small, localized areas of
      visible mold growth, in accordance with Florida law. For larger or
      more extensive mold issues, we will hire a licensed mold remediation
      professional.
    </p>
  </FadeIn>
</section>
    </>
  )
}

function GalleryPage() {
  const images = [
    {
      src: 'https://atticareusa.com/wp-content/uploads/2022/03/vapor-before-after-.jpg',
      alt: 'Crawlspace vapor barrier before and after project in Northeast Florida',
    },
    {
      src: 'https://thefoundationspecialists.com/wp-content/uploads/2024/01/Before-After-8.png',
      alt: 'Crawlspace encapsulation before and after comparison',
    },
    {
      src: 'https://vfsworks.com/wp-content/uploads/2025/04/before-and-after-crawl-space-encapsulation.webp',
      alt: 'Crawlspace encapsulation transformation with new liner and dehumidifier',
    },
    {
      src: 'https://conservationpros.com/wp-content/uploads/2015/02/Woodfin-Sealed-Crawlspace-Before-And-After-1.jpg',
      alt: 'Sealed crawlspace before and after project',
    },
    {
      src: 'https://www.groundworks.com/wp-content/uploads/sites/13/2021/08/Encapsulated-crawl-space-in-crawl-space-finished-job-1.jpg',
      alt: 'Finished encapsulated crawlspace with clean white liner',
    },
    {
      src: 'https://www.thedrytech.com/wp-content/uploads/2022/04/crawl-space-encapsulation-system.jpg',
      alt: 'Crawlspace encapsulation system installed under a home',
    },

    // ✅ Local attic before/after images
    {
      src: atticBeforeAfter1,
      alt: 'Before and after spray foam insulation in a Florida attic roof deck',
    },
    {
      src: atticBeforeAfter2,
      alt: 'Side-by-side attic transformation with spray foam insulation at the roofline',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Crawlspace & Attic Before & After Gallery | Northeast Florida Projects</title>
        <meta
          name="description"
          content="View before and after photos of crawlspace encapsulation and attic spray foam projects completed across Jacksonville, St. Augustine, and Northeast Florida."
        />
        <link rel="canonical" href="https://flhealthyhomesolutions.com/gallery" />
      </Helmet>

      <section className="section section-light">
        <FadeIn className="section-header">
          <h2>Before &amp; After Projects</h2>
          <p>
            See how our team transforms damp, moldy crawlspaces and hot, energy-wasting attics
            into clean, dry, efficient, and healthy environments.
          </p>
        </FadeIn>

        <div className="gallery-grid">
          {images.map((img, i) => (
            <FadeIn key={i}>
              <img
                src={img.src}
                alt={img.alt}
                className="gallery-image"
              />
            </FadeIn>
          ))}
        </div>
      </section>
    </>
  );
}



// ---------- BLOG LIST PAGE (/blog) ----------

function BlogPage() {
  return (
    <>
      <Helmet>
        <title>
          Crawlspace & Attic Conditioning Blog | Moisture, Insulation & Healthy Home Tips
        </title>
        <meta
          name="description"
          content="Learn about crawlspaces, attic insulation, spray foam, moisture control, and healthy home building-science tips from licensed contractors serving Jacksonville & St. Augustine, FL."
        />
        <link rel="canonical" href="https://flhealthyhomesolutions.com/blog" />
      </Helmet>

      <section className="section section-white">
        <FadeIn className="section-header">
          <h2>Healthy Home Insights</h2>
          <p>
            Articles to help North Florida homeowners understand crawlspaces, attics, humidity,
            insulation, and indoor air quality—so your home stays healthy from the ground up
            to the roofline.
          </p>
        </FadeIn>

        <div className="blog-grid">
          {blogPosts.map((post) => (
            <FadeIn key={post.slug}>
              <Link to={`/blog/${post.slug}`} className="blog-card-link">
                <div className="card blog-card">
                  <h3>{post.title}</h3>
                  <p>{post.summary}</p>
                  <button className="btn-link">Read more</button>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </section>
    </>
  )
}


// ---------- BLOG DETAIL PAGE (/blog/:slug) ----------

function BlogPostPage() {
  const { slug } = useParams()
  const post = blogPosts.find((p) => p.slug === slug)

  if (!post) {
    return (
      <>
        <Helmet>
          <title>Article Not Found | Florida Healthy Home Solutions Blog</title>
          <meta
            name="description"
            content="The article you are looking for could not be found on the Florida Healthy Home Solutions blog."
          />
        </Helmet>

        <section className="section section-white">
          <FadeIn className="section-header">
            <h2>Article Not Found</h2>
            <p>The blog post you are looking for does not exist.</p>
            <Link to="/blog" className="btn-link">
              ← Back to all articles
            </Link>
          </FadeIn>
        </section>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>{post.title} | Florida Healthy Home Solutions</title>
        <meta
          name="description"
          content={
            post.summary ||
            'Expert attic and crawlspace tips for Florida homeowners from licensed building-science contractors.'
          }
        />

        <link
          rel="canonical"
          href={`https://flhealthyhomesolutions.com/blog/${post.slug}`}
        />

        {/* ARTICLE SCHEMA */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.summary,
            "author": {
              "@type": "Organization",
              "name": "Florida Healthy Home Solutions"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Florida Healthy Home Solutions",
              "logo": {
                "@type": "ImageObject",
                "url": "https://flhealthyhomesolutions.com/logo.png"
              }
            },
            "datePublished": "2025-01-15",
            "dateModified": "2025-01-15"
          })}
        </script>
      </Helmet>

      <section className="section section-white">
        <FadeIn className="section-header">
          <button className="btn-secondary" onClick={() => window.history.back()}>
            ← Back to Articles
          </button>
          <h2>{post.title}</h2>
        </FadeIn>

        <FadeIn className="blog-article">
          <div
            className="blog-html"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </FadeIn>
      </section>
    </>
  )
}

// ---------- LOCAL SEO PAGES ----------

function LocalCrawlspacePage({
  title,
  subtitle,
  intro,
  countyLine,
  problems,
  areas,
  seoTitle,
  seoDescription,
  canonicalUrl,
  cityName,
}) {
  return (
    <>
      <Helmet>
        <title>{seoTitle || `${title} | Florida Healthy Home Solutions`}</title>
        <meta
          name="description"
          content={
            seoDescription ||
            'Licensed crawlspace encapsulation, vapor barriers and moisture control for homes throughout Northeast Florida.'
          }
        />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* BREADCRUMB SCHEMA */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://flhealthyhomesolutions.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": `${cityName} Crawlspace Services`,
                "item": canonicalUrl
              }
            ]
          })}
        </script>
      </Helmet>

      <section className="section section-white">
        <FadeIn className="section-header">
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </FadeIn>

        <FadeIn className="section-text-wide">
          <p>{intro}</p>
          <p>{countyLine}</p>
        </FadeIn>

        <div className="grid-three">
          <FadeIn>
            <div className="card icon-card">
              <Droplet className="icon" />
              <h3>Common Crawlspace Problems</h3>
              <ul>
                {problems.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          </FadeIn>
          <FadeIn>
            <div className="card icon-card">
              <Shield className="icon" />
              <h3>Our Building-Science Solution</h3>
              <p>
                We do more than roll out plastic. Every project starts with inspection and
                documentation, then a step-by-step plan that may include grading, drainage,
                a 12-mil or thicker vapor barrier, wall insulation, sealed vents, and a
                properly sized dehumidifier. The goal is a dry, stable crawlspace that
                protects your floors and indoor air year-round.
              </p>
            </div>
          </FadeIn>
          <FadeIn>
            <div className="card icon-card">
              <HomeIcon className="icon" />
              <h3>Benefits for Your Home</h3>
              <ul>
                <li>More stable hardwood or laminate floors</li>
                <li>Less musty odor in living spaces</li>
                <li>Better indoor air quality for your family</li>
                <li>Protection for joists, beams, and subflooring</li>
                <li>Improved comfort and energy efficiency</li>
              </ul>
            </div>
          </FadeIn>
        </div>

        <FadeIn className="section-subheader">
          <h3>Areas We Serve in This Region</h3>
        </FadeIn>
        <FadeIn className="section-list">
          <ul>
            {areas.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </FadeIn>

        <FadeIn className="section-text-wide">
          <p>
            If you are seeing any warning signs—musty odors, soft spots in flooring, cold or
            damp floors, or visible moisture in the crawlspace—it is a good time to schedule
            a professional evaluation. We will inspect, take photos, and explain your
            options in plain language so you can make a confident decision.
          </p>
          <p>
            <strong>Ready to get started?</strong> Use the button below to request your free
            crawlspace evaluation, and we will contact you to schedule a convenient time.
          </p>
          <Link to="/contact">
            <button className="btn-primary">Book My Local Crawlspace Evaluation</button>
          </Link>
        </FadeIn>
      </section>
    </>
  )
}

function StAugustinePage() {
  return (
    <LocalCrawlspacePage
      seoTitle="St. Augustine Crawlspace Encapsulation & Attic Insulation | Licensed FL Contractor"
      seoDescription="Licensed crawlspace encapsulation and attic insulation contractor in St. Augustine, FL. Vapor barriers, dehumidifiers, spray foam attic conditioning & moisture control for historic and coastal homes. Free inspection!"
      canonicalUrl="https://flhealthyhomesolutions.com/crawlspace-encapsulation-st-augustine-fl"
      cityName="St. Augustine"
      title="Crawlspace Encapsulation & Attic Insulation in St. Augustine, FL"
      subtitle="Protecting historic and coastal homes in St. Augustine from moisture, mold, cupping wood floors, and hot, humid attics."
      intro="St. Augustine homes, especially older crawlspace homes and properties near the Intracoastal, are constantly exposed to high humidity and salt-laden air. That moisture finds its way under your home first—and heat and humidity often build up in the attic—showing up later as musty smells, soft spots in flooring, buckled hardwood, and uncomfortable rooms below the attic."
      countyLine="Florida Healthy Home Solutions provides full crawlspace and attic inspection, encapsulation, attic insulation and spray foam roof deck upgrades, and dehumidification services for homeowners in St. Augustine and the surrounding St. Johns County area."
      problems={[
        'Musty odors that are stronger on the first floor',
        'Cupping or buckling hardwood and engineered floors',
        'Condensation on ductwork and pipes in the crawlspace',
        'Visible mold on joists, beams, or subfloor edges',
        'Animals or pests nesting in old insulation',
        'Very hot, humid attic that makes upstairs hard to cool',
        'Old or thin attic insulation that no longer protects the home',
      ]}
      areas={[
        'St. Augustine historic district',
        'St. Augustine Beach',
        'Vilano Beach',
        'World Golf Village area',
        'Palencia and nearby communities',
      ]}
    />
  )
}

function JacksonvillePage() {
  return (
    <LocalCrawlspacePage
      seoTitle="Jacksonville Crawlspace Encapsulation & Attic Insulation | Licensed FL Contractor"
      seoDescription="Professional crawlspace encapsulation and attic insulation in Jacksonville, FL. Vapor barriers, insulation, spray foam roof decks, dehumidifiers & structural repairs. Licensed contractor. Free estimate!"
      canonicalUrl="https://flhealthyhomesolutions.com/crawlspace-encapsulation-jacksonville-fl"
      cityName="Jacksonville"
      title="Crawlspace Encapsulation & Attic Services in Jacksonville, FL"
      subtitle="Solving moisture, flooring, and attic comfort issues for Jacksonville crawlspace homes."
      intro="Jacksonville's mix of older neighborhoods and newer construction means many homes still rely on vented crawlspaces and poorly insulated attics. In our hot, humid climate, open vents and thin insulation pull in wet outdoor air that condenses on cooler surfaces under the house and allows extreme heat to build up in the attic."
      countyLine="We help Jacksonville homeowners fix wet, moldy, or poorly insulated crawlspaces and hot, humid attics by combining proper drainage, heavy-duty vapor barriers, air sealing, attic insulation or spray foam, and dehumidification into one complete system."
      problems={[
        'Damp, musty smell that lingers after rain',
        'Cold, drafty floors over the crawlspace',
        'Visible standing water or damp soil',
        'Falling fiberglass insulation under the floor',
        'High indoor humidity even with AC running',
        'Rooms below the attic that are much hotter than the rest of the home',
        'Old, dirty, or insufficient attic insulation in older Jacksonville neighborhoods',
      ]}
      areas={[
        'San Marco',
        'Riverside & Avondale',
        'Arlington',
        'Southside',
        'North Jacksonville and surrounding areas',
      ]}
    />
  )
}

function StJohnsCountyPage() {
  return (
    <LocalCrawlspacePage
      seoTitle="St. Johns County Crawlspace Encapsulation & Attic Insulation | Licensed Contractor"
      seoDescription="Crawlspace encapsulation and attic insulation in St. Johns County, FL. Licensed contractor providing vapor barriers, attic upgrades & moisture control. Serving all of St. Johns County."
      canonicalUrl="https://flhealthyhomesolutions.com/crawlspace-encapsulation-st-johns-county-fl"
      cityName="St. Johns County"
      title="Crawlspace Encapsulation & Attic Upgrades in St. Johns County, FL"
      subtitle="Moisture control, crawlspace upgrades, and attic insulation for rapidly growing St. Johns County neighborhoods."
      intro="St. Johns County has seen explosive growth, but even newer homes can struggle with damp crawlspaces and hot attics if drainage, grading, ventilation, and insulation were not designed with our humidity in mind. Moisture under the home and extreme attic heat both impact comfort and flooring performance."
      countyLine="Florida Healthy Home Solutions provides inspections, moisture readings, and complete crawlspace encapsulation and attic improvement packages tailored to St. Johns County's soils, weather patterns, and building styles."
      problems={[
        'Persistent humidity under new or renovated homes',
        'Construction debris left in the crawlspace',
        'Inadequate or thin ground plastic with open seams',
        'Localized mold growth where humidity stays high',
        'Termite inspection areas covered by plastic',
        'Bonus rooms or second floors that stay too hot in summer',
        'Attic insulation that has settled, shifted, or been disturbed by trades over time',
      ]}
      areas={[
        'St. Johns',
        'Fruit Cove & Julington Creek area',
        'Nocatee area within St. Johns County',
        'Durbin Crossing',
        'County-wide rural and suburban crawlspace homes',
      ]}
    />
  )
}

function ClayCountyPage() {
  return (
    <LocalCrawlspacePage
      seoTitle="Clay County Crawlspace Encapsulation & Attic Insulation | Licensed FL Contractor"
      seoDescription="Licensed contractor serving Clay County, FL with crawlspace encapsulation, attic insulation, vapor barriers, dehumidifiers & structural repairs. Green Cove Springs, Fleming Island & more."
      canonicalUrl="https://flhealthyhomesolutions.com/crawlspace-encapsulation-clay-county-fl"
      cityName="Clay County"
      title="Crawlspace Encapsulation & Attic Services in Clay County, FL"
      subtitle="Serving Green Cove Springs, Fleming Island, and all of Clay County with professional crawlspace and attic solutions."
      intro="Clay County crawlspaces often see a combination of high groundwater, shaded lots, and aging ventilation systems. At the same time, many homes have poorly insulated or vented attics that trap heat and humidity. Together, those conditions can create the perfect environment for mold, wood rot, sagging insulation, and high energy bills."
      countyLine="As a licensed residential contractor and building analyst, we design crawlspace and attic systems for Clay County that keep the structure dry, protect flooring, improve comfort, and support healthy indoor air quality over the long term."
      problems={[
        'Standing water or damp mud under the home',
        'Efflorescence or staining on foundation walls',
        'Mold growth on joists and subfloor edges',
        'Loose, hanging fiberglass full of debris',
        'Dehumidifiers not installed or not draining correctly',
        'Very hot attic spaces over bedrooms or living areas',
        'Old or damaged attic insulation, especially above additions or garage rooms',
      ]}
      areas={[
        'Green Cove Springs',
        'Fleming Island',
        'Orange Park',
        'Middleburg',
        'Oakleaf and surrounding communities',
      ]}
    />
  )
}

function PutnamCountyPage() {
  return (
    <LocalCrawlspacePage
      seoTitle="Putnam County Crawlspace Encapsulation & Attic Insulation | Licensed Contractor"
      seoDescription="Crawlspace encapsulation and attic insulation in Putnam County, FL. Licensed contractor serving Palatka, Crescent City & Interlachen with vapor barriers, attic upgrades & moisture control."
      canonicalUrl="https://flhealthyhomesolutions.com/crawlspace-encapsulation-putnam-county-fl"
      cityName="Putnam County"
      title="Crawlspace Encapsulation in Putnam County, FL"
      subtitle="Helping Putnam County homeowners dry out damp crawlspaces, improve attic performance, and protect older homes."
      intro="Many Putnam County homes were built with little attention to crawlspace moisture control or attic insulation. Over time, high humidity, minor flooding, poor ventilation, and thin insulation can quietly damage the structure from below and make living spaces harder to heat and cool."
      countyLine="We provide detailed inspections and phased solutions so Putnam County homeowners can prioritize the most important crawlspace and attic repairs while still moving toward a fully encapsulated, better insulated, and controlled home."
      problems={[
        'Heavily rusted metal ducts and supports',
        'Rotting or softened subfloor near bathrooms or kitchens',
        'Moisture wicking up foundation walls',
        'Thin, torn plastic that no longer blocks vapor',
        'Insect or rodent activity in the crawlspace',
        'Attic spaces with little or no insulation in older farm or rural homes',
        'Uneven temperatures from room to room due to attic and crawlspace issues combined',
      ]}
      areas={[
        'Palatka',
        'Crescent City',
        'Interlachen',
        'East Palatka and surrounding areas',
        'Rural crawlspace homes throughout Putnam County',
      ]}
    />
  )
}

function VolusiaCountyPage() {
  return (
    <LocalCrawlspacePage
      seoTitle="Volusia County Crawlspace Encapsulation & Attic Insulation | Licensed Contractor"
      seoDescription="Licensed crawlspace and attic contractor in Volusia County, FL. Serving Daytona Beach, Ormond Beach, Port Orange & Deland with encapsulation, attic insulation, vapor barriers & dehumidifiers."
      canonicalUrl="https://flhealthyhomesolutions.com/crawlspace-encapsulation-volusia-county-fl"
      cityName="Volusia County"
      title="Crawlspace Encapsulation & Attic Services in Volusia County, FL"
      subtitle="Coastal and inland crawlspace and attic protection for homes in Volusia County."
      intro="From coastal winds along the Atlantic to inland humidity and afternoon storms, Volusia County homes experience year-round moisture pressure and intense attic heat. That moisture often shows up first in the crawlspace—long before you see problems inside the home—while the attic can quietly drive up energy bills and strain HVAC systems."
      countyLine="Florida Healthy Home Solutions designs crawlspace encapsulation and attic improvement systems that stand up to Volusia County's climate, with durable liners, sealed vents, upgraded attic insulation or spray foam, and active dehumidification where needed."
      problems={[
        'Musty odor stronger after onshore winds or heavy rain',
        'Corroded metal components in the crawlspace',
        'Condensation on cold ducts in summer',
        'Mold starting on joists or insulation facings',
        'Old, unsealed ground plastic or bare soil',
        'Attic temperatures that stay extremely high even late in the evening',
        'Insufficient attic insulation in coastal or older inland homes',
      ]}
      areas={[
        'Daytona Beach',
        'Ormond Beach',
        'Port Orange',
        'Deland',
        'Deltona and surrounding areas',
      ]}
    />
  )
}

function FlaglerCountyPage() {
  return (
    <LocalCrawlspacePage
      seoTitle="Flagler County Crawlspace Encapsulation & Attic Insulation | Licensed Contractor"
      seoDescription="Crawlspace encapsulation and attic insulation in Flagler County, FL. Licensed contractor serving Palm Coast, Flagler Beach & Bunnell with vapor barriers, attic upgrades & moisture control."
      canonicalUrl="https://flhealthyhomesolutions.com/crawlspace-encapsulation-flagler-county-fl"
      cityName="Flagler County"
      title="Crawlspace Encapsulation & Attic Services in Flagler County, FL"
      subtitle="Protecting Flagler County crawlspace homes and attics from coastal humidity, salty air, and soil moisture."
      intro="Flagler County's proximity to the ocean and Intracoastal Waterway means higher average humidity, salt-laden air, and more frequent wind-driven rain. Without proper crawlspace protection and attic insulation, that moisture and heat can damage the structure, impact indoor air quality, and make the home harder to cool."
      countyLine="We help Flagler County homeowners by sealing, insulating, and dehumidifying crawlspaces and upgrading attics so the space above and below the home both support a healthy, comfortable living environment."
      problems={[
        'Rust, corrosion, or staining on metal supports',
        'Soft or bouncy floors over the crawlspace',
        'High humidity and musty odors on the first floor',
        'Improperly installed or missing vapor barriers',
        'Vent openings that let in salty, humid air',
        'Uneven comfort between ocean-facing and interior rooms due to attic heat',
        'Attic insulation disturbed or damaged by past roof leaks or storms',
      ]}
      areas={[
        'Palm Coast',
        'Flagler Beach',
        'Bunnell',
        'Hammock area communities',
        'Crawlspace homes throughout Flagler County',
      ]}
    />
  )
}


// ---------- CONTACT & MISC ----------

function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await fetch('https://formsubmit.co/frederic@flhealthyhomesolutions.com', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })
      alert('✅ Thank you! Your message has been sent successfully.')
      setFormData({ name: '', email: '', phone: '', message: '' })
    } catch (err) {
      alert('There was a problem sending your message. Please try again.')
    }
  }

  return (
    <>
      <Helmet>
        <title>
          Contact Us | Free Crawlspace & Attic Evaluation in Northeast Florida
        </title>
        <meta
          name="description"
          content="Schedule a free crawlspace or attic inspection in Jacksonville, St. Augustine, or anywhere in Northeast Florida. Call 904-238-4416 or fill out our contact form today!"
        />
        <link rel="canonical" href="https://flhealthyhomesolutions.com/contact" />
      </Helmet>

      <section className="section section-light">
        <FadeIn className="section-header">
          <h1>Contact Us</h1>
          <p>
            Ready to create a healthier, more efficient home? Contact Florida Healthy 
            Home Solutions today for your free crawlspace or attic evaluation, or to 
            learn more about our moisture control, insulation, and building-science 
            services.
          </p>
        </FadeIn>

        <FadeIn className="section-text-wide">
          <p>
            We proudly serve Jacksonville, St. Augustine, and all of Northeast Florida — 
            including Duval, Clay, St. Johns, Putnam, Flagler, and Volusia County.
          </p>
        </FadeIn>

        <div className="contact-layout">
          <FadeIn className="contact-form">
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder="Full Name"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
              <input
                type="email"
                placeholder="Email Address"
                required
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
              <input
                type="tel"
                placeholder="Phone Number"
                required
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
              />
              <textarea
                placeholder="Your Message"
                rows={4}
                required
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
              />
              <button type="submit" className="btn-primary btn-full">
                Submit Request
              </button>
            </form>
          </FadeIn>

          <FadeIn className="contact-info">
            <div className="contact-item">
              <Phone className="icon" />
              <a href="tel:9042384416">904-238-4416</a>
            </div>
            <div className="contact-item">
              <Mail className="icon" />
              <a href="mailto:frederic@flhealthyhomesolutions.com">
                frederic@flhealthyhomesolutions.com
              </a>
            </div>
            <div className="contact-item">
              <MapPin className="icon" />
              <span>1200 Eagle Point Dr, Saint Augustine, FL 32092</span>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  )
}


function FreeInspectionCTA() {
  return (
    <section className="cta">
      <h3>Schedule Your Free Crawlspace & Attic Evaluation</h3>
      <p>
        Not sure what’s happening under your home or up in the attic? We’ll inspect your 
        crawlspace and/or attic, document what we find, and walk you through clear options 
        to protect your floors, air quality, energy efficiency, and overall structure.
      </p>
      <Link to="/contact">
        <button className="btn-primary">Book My Free Evaluation</button>
      </Link>
    </section>
  )
}


function Footer() {
  return (
    <footer className="footer">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <img
            src={logo}
            alt="Florida Healthy Home Solutions - Crawlspace & Attic Contractor Logo"
            style={{ height: '72px', width: 'auto' }}
          />
          <p className="footer-title">Florida Healthy Home Solutions</p>
        </div>

        <p>1200 Eagle Point Dr, Saint Augustine, FL 32092</p>

        <p>
          📞{' '}
          <a href="tel:9042384416" style={{ color: '#e5e7eb' }}>
            904-238-4416
          </a>{' '}
          | 🌐 www.flhealthyhome.com
        </p>

        <p className="footer-sub">
          Your Local Crawlspace, Attic &amp; Indoor Air Quality Experts
        </p>

        {/* Service area internal links for SEO */}
        <p className="footer-sub">
          Crawlspace Encapsulation &amp; Attic Services in:{' '}
          <Link to="/crawlspace-encapsulation-st-augustine-fl">St. Augustine</Link> ·{' '}
          <Link to="/crawlspace-encapsulation-jacksonville-fl">Jacksonville</Link> ·{' '}
          <Link to="/crawlspace-encapsulation-st-johns-county-fl">St. Johns County</Link> ·{' '}
          <Link to="/crawlspace-encapsulation-clay-county-fl">Clay County</Link> ·{' '}
          <Link to="/crawlspace-encapsulation-putnam-county-fl">Putnam County</Link> ·{' '}
          <Link to="/crawlspace-encapsulation-volusia-county-fl">Volusia County</Link> ·{' '}
          <Link to="/crawlspace-encapsulation-flagler-county-fl">Flagler County</Link>
        </p>
      </div>
    </footer>
  )
}


// ---------- APP ROOT ----------

function App() {
  return (
    <Router>
      <Navbar />
      <PageTransition>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* Local SEO routes */}
          <Route
            path="/crawlspace-encapsulation-st-augustine-fl"
            element={<StAugustinePage />}
          />
          <Route
            path="/crawlspace-encapsulation-jacksonville-fl"
            element={<JacksonvillePage />}
          />
          <Route
            path="/crawlspace-encapsulation-st-johns-county-fl"
            element={<StJohnsCountyPage />}
          />
          <Route
            path="/crawlspace-encapsulation-clay-county-fl"
            element={<ClayCountyPage />}
          />
          <Route
            path="/crawlspace-encapsulation-putnam-county-fl"
            element={<PutnamCountyPage />}
          />
          <Route
            path="/crawlspace-encapsulation-volusia-county-fl"
            element={<VolusiaCountyPage />}
          />
          <Route
            path="/crawlspace-encapsulation-flagler-county-fl"
            element={<FlaglerCountyPage />}
          />
        </Routes>
      </PageTransition>
      <FreeInspectionCTA />
      <Footer />
    </Router>
  )
}

export default App